import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Loader, Center, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ProductForm } from '@/components/admin/ProductForm';
import { getProductById, updateProduct } from '@/lib/admin/products';
import { Product } from '@/types';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAuth } from '@/context/AuthContext';

export default function AdminProductEditPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { user, isAdmin: isAdminUser, loading: authLoading } = useAuth();
  const productId = params?.id as string;
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || !isAdminUser)) {
      navigate('/xyz/login');
      return;
    }

    if (productId && user && isAdminUser) {
      loadProduct();
    }
  }, [productId, user, isAdminUser, authLoading, navigate]);

  const loadProduct = async () => {
    if (!productId) return;
    
    try {
      const data = await getProductById(productId);
      if (!data) {
        notifications.show({
          title: 'Error',
          message: 'Product not found',
          color: 'red',
        });
        navigate('/xyz/products');
        return;
      }
      setProduct(data);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to load product',
        color: 'red',
      });
      navigate('/xyz/products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: Partial<Product>) => {
    if (!productId) return;
    
    setSaving(true);
    try {
      await updateProduct(productId, data);
      notifications.show({
        title: 'Success',
        message: 'Product updated successfully',
        color: 'green',
      });
      navigate('/xyz/products');
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update product',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/xyz/products');
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <Container size="xl" py="xl">
          <Center h="50vh">
            <Loader size="lg" />
          </Center>
        </Container>
      </AdminLayout>
    );
  }

  if (!user || !isAdminUser) {
    return null;
  }

  if (!product) {
    return (
      <AdminLayout>
        <Container size="xl" py="xl">
          <Center h="50vh">
            <Text>Product not found</Text>
          </Center>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <ProductForm
        product={product}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={saving}
      />
    </AdminLayout>
  );
}
